import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { KeyRound, Loader2, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface Props {
  shelterId: string;
  shelterName: string;
  defaultEmail?: string | null;
}

const getErrorMessage = (e: unknown, fb: string) =>
  e instanceof Error ? e.message : fb;

export const InlineGenerateClaimLinkButton = ({
  shelterId,
  shelterName,
  defaultEmail,
}: Props) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [note, setNote] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setEmail(defaultEmail ?? "");
      setNote("");
      setGeneratedUrl(null);
      setCopied(false);
    }
  };

  const generate = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { shelter_id: shelterId };
      if (email.trim()) body.recipient_email = email.trim();
      if (note.trim()) body.note = note.trim();
      const { data, error } = await supabase.functions.invoke("claim-shelter-token", { body });
      if (error) throw error;
      return data as { claim_url: string };
    },
    onSuccess: async (data) => {
      setGeneratedUrl(data.claim_url);
      try {
        await navigator.clipboard.writeText(data.claim_url);
        setCopied(true);
        toast.success(
          email.trim()
            ? "Link wygenerowany, wysłany mailem i skopiowany"
            : "Link wygenerowany i skopiowany",
        );
      } catch {
        toast.success("Link wygenerowany");
      }
      queryClient.invalidateQueries({ queryKey: ["admin-claim-tokens"] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Nie udało się wygenerować")),
  });

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast.success("Skopiowano");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        title="Wygeneruj magic link do przejęcia profilu"
        className="text-muted-foreground"
      >
        <KeyRound className="w-4 h-4" />
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Magic link dla: {shelterName}</DialogTitle>
          <DialogDescription>
            Link jest ważny 30 dni. Po podaniu emaila zostanie automatycznie wysłany do organizacji.
          </DialogDescription>
        </DialogHeader>

        {generatedUrl ? (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Wygenerowany link</label>
              <div className="flex gap-2 items-center">
                <Input
                  readOnly
                  value={generatedUrl}
                  className="text-xs font-mono"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button size="sm" variant="outline" onClick={handleCopy} className="shrink-0">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            {email.trim() && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> Wysłano na: {email.trim()}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Email odbiorcy (opcjonalnie)</label>
              <Input
                type="email"
                placeholder="kontakt@schronisko.pl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {defaultEmail
                  ? "Pre-wypełnione z danych schroniska. Wyczyść aby tylko skopiować link."
                  : "Jeśli puste - link nie zostanie wysłany, tylko skopiowany."}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notatka wewnętrzna (opcjonalnie)</label>
              <Textarea
                placeholder="np. Wysłany przez Instagram"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={300}
                rows={2}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {generatedUrl ? (
            <Button onClick={() => handleOpen(false)}>Zamknij</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => handleOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
                {generate.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Wygeneruj
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
